// Three.js renderer for Lumen Abyss Gate 1.
// Camera follows player, instanced enemies/projectiles/pickups, particles, trails, bloom.

import * as THREE from 'three';
import { cfg } from '../config.js';
import { WorldState } from '../sim/state.js';
import { EventBus, GameEvent } from '../sim/events.js';
import { PostProcess } from './post-process.js';
import { InstanceRenderers, PROJECTILE_COLOR, pickupColor, enemyColor, enemyMatrix, projectileMatrix, pickupMatrix } from './instances.js';
import { ParticleSystem } from './particles.js';
import { TrailSystem } from './trails.js';
import { generateShipSpec, shipShapeFromSpec, SHIP_SCALE, type ShipSpec, type ShipBias } from './ship-generator.js';
import { HpArcs } from './hp-arcs.js';
import { DamageNumbers } from './damage-numbers.js';
import { FloatingText } from './floating-text.js';
import { DeathRings } from './death-rings.js';
import { BackgroundGrid } from './background-grid.js';
import { BackgroundObjects } from './background-objects.js';
import { MicroEvents } from './micro-events.js';
import { SpawnPortals, PORTAL_OPEN_TICKS } from './spawn-portals.js';
import { ZoneEffect } from './zone-effect.js';
import { WormholePortal } from './wormhole.js';
import { WarpTransition } from './warp-transition.js';
import { WARP_IN_TICKS, WARP_OUT_TICKS } from '../sim/bonus-game.js';
import { BoonVisuals } from './boon-visuals.js';
import { PlayerHalo } from './player-halo.js';
import { MagnetRing } from './magnet-ring.js';
import { PlayerDeathFX } from './player-death-fx.js';
import { SpaceStormFX } from './space-storm.js';
import type { SpaceStormRenderState } from '../sim/space-storm.js';
import { smoothstep, easeOutCubic } from './easing.js';
import { wrappedDelta } from '../sim/collision.js';
import { getUpgradeMods } from '../sim/upgrade-system.js';
import { getShipDecorations, type Decoration } from '../sim/decorations.js';
import type { Vec2, Settings } from '../types.js';

const BLOOM_MAX_STRENGTH = 0.85;
const WHITE = new THREE.Color(0xffffff);
// Portrait (phone) framing: enemies scale down slightly on tall/narrow
// viewports so the swarm stays proportional to the ship (the un-zoomed
// portrait view otherwise makes every actor read ~2× its desktop size).
const PORTRAIT_ENEMY_SCALE = 0.85;

// Camera lag: while moving, the focus point trails BEHIND the velocity by
// this fraction of the half-view (playtest found lookahead ran away from the
// ship and made mouse steering feel like slowdown). The offset is damped with
// the time constant below and capped at 10% of the view extent per axis; it is
// exactly zero when stationary, where the focus instead eases ~30% toward the
// centroid of on-screen enemies so a dense swarm stays framed instead of
// piling into a corner of the screen.
const CAMERA_LEAD_FRACTION = 0.07;
const CAMERA_LEAD_MAX_FRACTION = 0.10;
const CAMERA_LEAD_TAU_S = 0.15;
const CAMERA_LEAD_SWARM_WEIGHT = 0.3;

// Wormhole subspace: fade-in/out window for the portal open envelopes
// (~0.4 s at the 60 Hz sim step rate) and the duration of a portal's visual
// collapse (entrance wormhole after entry, exit portal after use/exit).
const BONUS_FADE_TICKS = 24;
const PORTAL_COLLAPSE_TICKS = 18;

/** Per-portal open-envelope state (eased on the sim tick, never wall-clock). */
interface PortalEnvelope {
  target: number;
  changeTick: number;
  changeFrom: number;
  strength: number;
}

/** Ease a portal's open envelope toward its target over BONUS_FADE_TICKS;
 * rebases on tick rollback (run restart) so the fade never runs backwards. */
function easePortalEnvelope(env: PortalEnvelope, open: boolean, tick: number, tickF: number): number {
  const target = open ? 1 : 0;
  if (target !== env.target || tick < env.changeTick) {
    env.target = target;
    env.changeTick = tick;
    env.changeFrom = env.strength;
  }
  const k = Math.min(1, Math.max(0, tickF - env.changeTick) / BONUS_FADE_TICKS);
  const eased = k * k * (3 - 2 * k);
  env.strength = env.changeFrom + (target - env.changeFrom) * eased;
  return env.strength;
}

/** smoothstep(0, 1, x) with clamping, for phase-progress envelopes. */
function smoothstep01(x: number): number {
  const t = Math.min(1, Math.max(0, x));
  return t * t * (3 - 2 * t);
}

function vecFromPayload(p: Record<string, unknown>): Vec2 | undefined {
  if (typeof p.x === 'number' && typeof p.y === 'number') {
    return { x: p.x, y: p.y };
  }
  // Most sim events carry the position nested under `pos`.
  const pos = p.pos as Record<string, unknown> | undefined;
  if (pos && typeof pos.x === 'number' && typeof pos.y === 'number') {
    return { x: pos.x, y: pos.y };
  }
  return undefined;
}

export class GameRenderer {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene: THREE.Scene;
  readonly camera: THREE.OrthographicCamera;
  private readonly postProcess: PostProcess;
  private readonly instances: InstanceRenderers;
  private readonly particles: ParticleSystem;
  private readonly trails: TrailSystem;
  private readonly damageNumbers: DamageNumbers;
  private readonly deathRings: DeathRings;
  private readonly backgroundObjects: BackgroundObjects;
  private readonly microEvents: MicroEvents;
  private readonly zoneEffect: ZoneEffect;
  private readonly boonVisuals: BoonVisuals;
  private readonly playerDeathFX: PlayerDeathFX;
  private readonly backgroundGrid: BackgroundGrid;
  private readonly portals: SpawnPortals;
  private readonly wormhole: WormholePortal;
  /** Subspace exit portal (escape hatch) — mint/cyan so it reads as the way out. */
  private readonly exitPortal: WormholePortal;
  /** Warp tunnel overlay for the subspace transitions (phase-driven). */
  private readonly warpFX: WarpTransition;
  private readonly spaceStormFX: SpaceStormFX;
  private readonly bonusTint: THREE.Mesh;
  private readonly bonusTintMaterial: THREE.ShaderMaterial;
  private readonly playerMesh: THREE.Group;
  private shipHull: THREE.Group | null = null;
  // Permanent soft glow under the ship so it never vanishes under the swarm.
  private readonly playerHalo = new PlayerHalo();
  // Faint dashed ring at the effective magnet pull radius (playtest aid).
  private readonly magnetRing = new MagnetRing();
  private readonly hpArcs = new HpArcs();
  // War-life decal layer (spec §3.3): a direct child of the player mesh —
  // NOT of the hull group — so rebuildShipHull never removes it. Rebuilt
  // only when the decoration count changes (shipDecalCount tracks the last
  // synced count; -1 forces the first sync to build).
  private readonly shipDecals = new THREE.Group();
  private shipDecalCount = -1;
  // One shared material per decoration kind; rebuilds only churn geometry.
  private readonly decalMaterials: Record<Decoration['kind'], THREE.MeshBasicMaterial> = {
    stud: new THREE.MeshBasicMaterial({ color: 0xffd36b, toneMapped: false, side: THREE.DoubleSide, transparent: true, depthWrite: false }),
    'badge-maw': new THREE.MeshBasicMaterial({ color: 0xff3d81, toneMapped: false, side: THREE.DoubleSide, transparent: true, depthWrite: false }),
    'badge-razor': new THREE.MeshBasicMaterial({ color: 0xff6b3d, toneMapped: false, side: THREE.DoubleSide, transparent: true, depthWrite: false }),
  };
  private readonly damageChips: THREE.Mesh[] = [];
  private readonly chipFades: number[] = [];
  private readonly turretMesh: THREE.Group;
  // Assigned inside createTurretMesh(), which the constructor runs.
  private turretBarrel!: THREE.Group;
  private readonly turretAttachments: Record<string, THREE.Mesh> = {};
  private readonly ambientLight: THREE.AmbientLight;
  private readonly arenaSize: number;
  private readonly viewHeight: number;
  private readonly dummyMatrix = new THREE.Matrix4();
  private readonly dummyColor = new THREE.Color();
  // Scratch scale matrix for the enemy spawn-in scale (0→1 over the portal-open window).
  private readonly spawnScaleMatrix = new THREE.Matrix4();
  /** enemy id → last hit tick: the struck enemy blinks white for ~6 ticks. */
  private readonly enemyHitTicks = new Map<number, number>();
  /** Deaths reaped within one sim tick — the third fires a camera impulse. */
  private deathTick = -1;
  private deathsThisTick = 0;

  private trauma = 0;
  private reducedShake = false;
  private reducedFlashes = false;
  private reducedParticles = false;
  // Visual-only enemy scale factor for portrait viewports (see resize()).
  private enemyViewScale = 1;
  private lastFrameTime = performance.now();
  // ?debug=1 on-device diagnostics: death events vs death bursts spawned vs
  // live particles + the last event-bus handler error. When a user reports
  // "animations stopped", these counters say which layer stopped.
  private dbgDeaths = 0;
  private dbgEl: HTMLElement | null = null;
  private readonly dbgEnabled =
    typeof location !== 'undefined' && new URLSearchParams(location.search).has('debug');
  // Continuous camera anchor: follows the player across the torus seam without
  // jumping, so the background and entities wrap smoothly.
  private readonly cameraAnchor: Vec2 = { x: 0, y: 0 };
  // Damped camera-lead offset (see CAMERA_LEAD_* constants). Applied to the
  // camera on top of the anchor; render-only, never fed back into the sim.
  private readonly cameraLead: Vec2 = { x: 0, y: 0 };
  // Smoothed world-space aim angle of the turret barrel; slews toward the
  // pointer instead of snapping so the manual gun reads as a rotating turret.
  private turretAimAngle = 0;
  // Jet feel: heading eases (never snaps), the ship banks into turns, and a
  // dash throws a full aileron roll around the nose axis.
  private shipHeading = 0;
  private bankAngle = 0;
  private prevDashActive = false;
  private maneuver: { kind: 'roll'; t: number; duration: number } | null = null;
  private frameDt = 1 / 60;
  private lastRunSeed = -1;
  private lastWorldTick = -1;
  // Trait bias shaping the hull silhouette (spec §3.1), stored for the next
  // run-seed rebuild. The loadout flow pushes it via setShipBias().
  private shipBias: ShipBias = { swift: 0, bulwark: 0, striker: 0 };
  // Last interpolated player position; used for death-proximity screen shake.
  private readonly lastPlayerPos: Vec2 = { x: 0, y: 0 };
  // Wormhole/subspace visuals are READ FROM THE SIM (world.wormhole,
  // world.bonus) inside updateBonusVisuals — nothing is pushed. The portal
  // open envelopes ease on world.tick only, so replays and pause render
  // identically. The wormhole's envelope is independent of the subspace tint
  // so the vortex renders during NORMAL play whenever a wormhole is active
  // (its pull field must never be invisible).
  private readonly wormholeEnv: PortalEnvelope = { target: 0, changeTick: 0, changeFrom: 0, strength: 0 };
  /** Subspace exit portal open envelope (same pattern as the wormhole's). */
  private readonly exitEnv: PortalEnvelope = { target: 0, changeTick: 0, changeFrom: 0, strength: 0 };
  // Space storm state, pushed by game.ts via setSpaceStormState() each frame.
  private spaceStormState: SpaceStormRenderState | null = null;

  constructor(canvas: HTMLCanvasElement, settings: Settings) {
    this.arenaSize = cfg.getNumber('sim.arena_size');
    this.viewHeight = 45;

    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: false,
      powerPreference: 'high-performance'
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    // updateStyle=false: the stylesheet owns the canvas box (inset:0, 100%x100%).
    // setSize's default inline px styles would freeze clientWidth/Height and
    // break every later window resize (canvas stuck as a top band).
    this.renderer.setSize(canvas.clientWidth, canvas.clientHeight, false);
    this.renderer.setClearColor(0x050a14);

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x050a14);

    const aspect = canvas.clientWidth / Math.max(canvas.clientHeight, 1);
    this.camera = new THREE.OrthographicCamera(
      -this.viewHeight * aspect * 0.5,
      this.viewHeight * aspect * 0.5,
      this.viewHeight * 0.5,
      -this.viewHeight * 0.5,
      -100,
      100
    );
    this.camera.position.z = 10;

    this.postProcess = new PostProcess(
      this.renderer,
      settings.video.bloom_intensity,
      settings.video.reduced_flashes
    );

    this.ambientLight = new THREE.AmbientLight(0xffffff, 0.35);
    this.scene.add(this.ambientLight);

    // Faint cool-blue directional fill so enemy bodies read cool against the
    // warm-gold player (the player is emissive-driven and stays warm).
    const coolRim = new THREE.DirectionalLight(0x4a7ea6, 0.5);
    coolRim.position.set(-0.6, 0.8, 1);
    this.scene.add(coolRim);

    this.backgroundGrid = new BackgroundGrid(this.arenaSize);
    this.scene.add(this.backgroundGrid.mesh);

    this.backgroundObjects = new BackgroundObjects();
    this.scene.add(this.backgroundObjects.group);

    this.zoneEffect = new ZoneEffect();
    this.scene.add(this.zoneEffect.mesh);

    this.portals = new SpawnPortals();
    this.scene.add(this.portals.mesh);

    // Wormhole subspace layer: fullscreen hue tint (driven by the bonus-phase
    // envelope), the entrance-wormhole vortex, the subspace exit portal, and
    // the warp tunnel overlay. All read sim state in updateBonusVisuals.
    this.bonusTintMaterial = this.createBonusTintMaterial();
    this.bonusTint = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this.bonusTintMaterial);
    this.bonusTint.frustumCulled = false;
    this.bonusTint.renderOrder = 1;
    this.bonusTint.visible = false;
    this.scene.add(this.bonusTint);

    this.wormhole = new WormholePortal();
    this.scene.add(this.wormhole.mesh);

    this.exitPortal = new WormholePortal({ a: 0x4dffb8, b: 0x4de3ff });
    this.scene.add(this.exitPortal.mesh);

    this.warpFX = new WarpTransition();
    this.scene.add(this.warpFX.mesh);

    // Space storm layer: fog of war, thunder flash, firebolts. Rendered above
    // the bonus tint and portals (renderOrder 3–5) so the fog darkens the arena.
    this.spaceStormFX = new SpaceStormFX();
    this.scene.add(this.spaceStormFX.group);

    this.instances = new InstanceRenderers(
      cfg.getNumber('perf.max_enemies'),
      cfg.getNumber('perf.max_projectiles'),
      512
    );
    this.scene.add(this.instances.enemyGroup);
    this.scene.add(this.instances.projectiles.mesh);
    this.scene.add(this.instances.pickupGroup);
    this.scene.add(this.hpArcs.mesh);

    const particleCapacity = settings.video.mobile_performance
      ? cfg.getNumber('perf.mobile_max_particles')
      : cfg.getNumber('perf.max_particles');
    this.particles = new ParticleSystem(particleCapacity);
    this.scene.add(this.particles.mesh);

    // Budget covers ~200 enemies × 22-segment streaks plus projectile/dash
    // trails (8192 segments × 6 verts, ~1.2 MB of buffers).
    this.trails = new TrailSystem(49152, 400);
    this.scene.add(this.trails.mesh);

    this.damageNumbers = new DamageNumbers(this.scene);

    this.deathRings = new DeathRings();
    this.scene.add(this.deathRings.mesh);

    this.playerDeathFX = new PlayerDeathFX(this.scene, this.particles, this.deathRings);

    this.microEvents = new MicroEvents(
      this.particles,
      this.trails,
      this.damageNumbers,
      new FloatingText(this.scene),
      this.scene
    );

    this.playerMesh = this.createPlayerMesh();
    this.scene.add(this.playerMesh);
    this.scene.add(this.playerHalo.mesh);
    this.scene.add(this.magnetRing.line);

    this.boonVisuals = new BoonVisuals();
    this.playerMesh.add(this.boonVisuals.group);

    this.turretMesh = this.createTurretMesh();
    this.playerMesh.add(this.turretMesh);
    // Mount the turret on top of the ship (tank-style), slightly above the hull.
    this.turretMesh.position.set(0, 0.25, 0.05);

    // The ship ALWAYS reads above the swarm: moving its whole subtree into
    // the transparent pass lets the painter's algorithm layer it over the
    // instanced enemies (ship parts sit at z ≥ 0.08, enemy layer maxes at
    // ≈0.08) instead of being painted first as opaque and buried.
    this.markShipLayer(this.playerMesh);

    this.applySettings(settings);
    this.resize();
  }

  bindEvents(bus: EventBus): void {
    bus.register((e: GameEvent) => {
      if (e.type === 'death') this.dbgDeaths++;
      const ctx = {
        playerPos: this.lastPlayerPos,
        playerVel: { x: 0, y: 0 },
      };
      this.microEvents.onEvent(e, ctx);
      switch (e.type) {
        case 'spawn': {
          if (e.payload.kind === 'enemy') {
            // Open a spawn portal under the emerging enemy. Portal timing comes
            // from the event tick / telegraph tick, never the wall clock.
            const pos = vecFromPayload(e.payload);
            if (pos) {
              const telegraphUntil = typeof e.payload.telegraph_until === 'number'
                ? e.payload.telegraph_until
                : e.tick;
              this.portals.open(pos, e.tick, telegraphUntil);
            }
          }
          break;
        }
        case 'death': {
          const pos = vecFromPayload(e.payload);
          const boss = e.payload.boss === true;
          // Kills get a small brightness kick; bosses a bigger one. The pulse
          // channel max()-merges, so mass-kill ticks never stack exposure.
          this.postProcess.pulseExposure(boss ? 0.3 : 0.14);
          // Multi-kill tick: one 2–4 px camera impulse when the same tick
          // reaps its third victim (a hitstop-free punch for chain clears).
          if (e.tick !== this.deathTick) {
            this.deathTick = e.tick;
            this.deathsThisTick = 0;
          }
          this.deathsThisTick++;
          if (this.deathsThisTick === 3) this.addTrauma(0.12);
          if (pos) {
            const enemyId = typeof e.payload.enemy_id === 'number' ? e.payload.enemy_id : undefined;
            if (enemyId !== undefined) {
              this.enemyHitTicks.delete(enemyId);
            }
            // Thin gold shockwave ring: heavy kills only — per-kill rings in
            // enemy hues were the fog carpet's second layer.
            if (boss) {
              this.deathRings.spawn(pos, new THREE.Color(0xffd36b), e.tick);
            }
            // Small screen shake for nearby deaths; decays in <0.2 s.
            const dx = pos.x - this.lastPlayerPos.x;
            const dy = pos.y - this.lastPlayerPos.y;
            const dist = Math.hypot(dx, dy);
            if (dist < 14) {
              this.addTrauma(0.18 * (1 - dist / 14));
            }
          }
          break;
        }
        case 'player_hit': {
          this.addTrauma(0.5);
          break;
        }
        case 'hit': {
          // Slight gamma lift per bullet hit (screen-blend punch).
          this.postProcess.pulseExposure(0.08);
          // The struck enemy blinks white for ~0.1 s (GW-style hit read).
          const targetId = typeof e.payload.target_id === 'number' ? e.payload.target_id : undefined;
          if (targetId !== undefined) this.enemyHitTicks.set(targetId, e.tick);
          break;
        }
        case 'nova': {
          // Serenity Anchor pulse: a golden ring centered on the ship (the
          // interpolated player position keeps it camera-correct at the seam).
          this.deathRings.spawn(
            { x: this.lastPlayerPos.x, y: this.lastPlayerPos.y },
            new THREE.Color(0xffd36b),
            e.tick
          );
          this.addTrauma(0.06);
          break;
        }
      }
    });
  }

  applySettings(settings: Settings): void {
    this.setRenderScale(settings.video.render_scale);
    this.setPixelatedUpscale(settings.video.pixelated_upscale);
    this.postProcess.setIntensity(settings.video.bloom_intensity);
    this.postProcess.setReducedFlashes(settings.video.reduced_flashes);
    this.postProcess.setCrtEnabled(settings.video.crt_enabled);
    this.postProcess.setScanlineIntensity(settings.video.crt_scanlines);
    this.postProcess.setChromaticAberration(settings.video.crt_aberration);
    this.postProcess.setNoise(settings.video.crt_noise);
    this.postProcess.setBarrelWarp(settings.video.crt_barrel_warp);
    this.postProcess.setVhsTracking(settings.video.crt_vhs_tracking);
    // Planet curvature keys are new; read defensively so persisted settings
    // written before they existed still behave (default: on, subtle).
    const video = settings.video as Settings['video'] & {
      planet_curvature?: boolean;
      planet_curvature_strength?: number;
    };
    this.postProcess.setPlanetCurvature(
      video.planet_curvature ?? true,
      video.planet_curvature_strength ?? 0.25
    );
    this.postProcess.setLomoLens(
      settings.video.lomo_lens ?? true,
      settings.video.lomo_lens_strength ?? 0.6
    );
    this.postProcess.setVcrGlitch(
      settings.video.vcr_glitch ?? true,
      settings.video.vcr_strength ?? 0.5,
      settings.video.vcr_glitch_strength ?? 0.8
    );
    this.reducedShake = settings.video.reduced_shake;
    this.reducedFlashes = settings.video.reduced_flashes;
    this.reducedParticles = settings.video.reduced_particles;
    this.particles.setReducedParticles(settings.video.reduced_particles);
    const trailScale = settings.video.reduced_particles ? 0.5 : 1.0;
    this.trails.setLengthScale(trailScale);
  }

  setBloomIntensity(intensity: number): void {
    this.postProcess.setIntensity(intensity);
  }

  /**
   * Retro render scale: 1.0 = native, 0.5 = half-res upscaled. Lower values
   * drastically reduce fragment cost (retro look + FPS win).
   */
  setRenderScale(scale: number): void {
    const clamped = Math.max(0.25, Math.min(scale, 2));
    this.renderer.setPixelRatio(clamped);
    this.resize();
  }

  /** Use nearest-neighbor upscaling for a chunky retro look. */
  setPixelatedUpscale(enabled: boolean): void {
    const canvas = this.renderer.domElement;
    canvas.style.imageRendering = enabled ? 'pixelated' : 'auto';
  }

  /** Active galaxy/nebula zones that grant a dash-recharge boost. */
  getGalaxyZones(): { x: number; y: number; radius: number }[] {
    return this.backgroundObjects.getActiveZones();
  }

  /** Active black holes: gravity wells that pull entities. */
  getGravityWells(): { x: number; y: number; radius: number; strength: number }[] {
    return this.backgroundObjects.getGravityWells();
  }

  /** Green/teal galaxy zones that restore HP when the player is on top of them. */
  getHealingZones(): { x: number; y: number; radius: number }[] {
    return this.backgroundObjects.getHealingZones();
  }

  /** Active boon objects with temporary effects. */
  getBoonZones(): { x: number; y: number; radius: number; kind: 'lucky-star' | 'aegis-moon' | 'swift-comet' }[] {
    return this.backgroundObjects.getBoonZones();
  }

  /** Start the dramatic player death sequence (bright burst, slow-mo, afterglow). */
  startPlayerDeath(pos: Vec2, tick: number): void {
    this.playerDeathFX.start(pos, tick);
  }

  /**
   * Space storm visuals. game.ts pushes the sim's storm snapshot here each
   * frame; the fog fade envelope eases on world.tick only, so replays and
   * pause behave identically to gameplay (same pattern as setBonusState).
   */
  setSpaceStormState(state: SpaceStormRenderState | null): void {
    this.spaceStormState = state;
  }

  addTrauma(amount: number): void {
    if (this.reducedFlashes) {
      amount *= 0.3;
    }
    this.trauma = Math.min(1, this.trauma + amount);
  }

  /** Radius of the circle covering the visible area: half the smaller view
   * dimension. Weapon targeting caps at this so the ship never attacks
   * enemies the player can't see. */
  getVisibleRadius(): number {
    return Math.min(
      this.camera.right - this.camera.left,
      this.camera.top - this.camera.bottom
    ) * 0.5;
  }

  /** Full-speed level-up cue: a gold ring off the ship and a light kick. */
  playLevelUpCue(tick: number): void {
    this.deathRings.spawn(
      { x: this.lastPlayerPos.x, y: this.lastPlayerPos.y },
      new THREE.Color(0xffd36b),
      tick
    );
    this.addTrauma(0.08);
  }

  resize(): void {
    const canvas = this.renderer.domElement;
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;
    const aspect = width / Math.max(height, 1);
    // Portrait (phone) framing: keep the full view height instead of zooming
    // in — the old 1.3× punch-in cropped the fight to a middle band with
    // empty arena above/below and made every enemy read ~2× its desktop size
    // next to the ship. Full height uses the tall screen for the play space;
    // a modest enemy-only scale-down restores ship/swarm proportion while the
    // halo keeps the ship unmistakable.
    const portrait = aspect < 1;
    this.enemyViewScale = portrait ? PORTRAIT_ENEMY_SCALE : 1;
    const effectiveViewHeight = this.viewHeight;
    this.camera.left = -effectiveViewHeight * aspect * 0.5;
    this.camera.right = effectiveViewHeight * aspect * 0.5;
    this.camera.top = effectiveViewHeight * 0.5;
    this.camera.bottom = -effectiveViewHeight * 0.5;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height, false); // CSS owns the box (see ctor)
    this.postProcess.resize(width, height);
    // Trail length follows the perf profile only (reduced_particles) — trail
    // ribbons are distance-sampled, so they don't need a portrait length cut;
    // narrow screens compensate via the same per-trail opacity instead.
    const trailScale = this.reducedParticles ? 0.5 : 1.0;
    this.trails.setLengthScale(trailScale);
  }

  /**
   * Store the trait bias used for the next hull rebuild (the run-seed watch
   * in render() passes it to generateShipSpec). Purely visual — the sim
   * owns the stat effects. Wired up by the loadout flow, not from world
   * state, so rendering stays seed-driven.
   */
  setShipBias(bias: ShipBias): void {
    this.shipBias = { ...bias };
  }

  render(world: WorldState, alpha: number): void {
    const now = performance.now();
    const dt = Math.min((now - this.lastFrameTime) / 1000, 0.05);
    this.lastFrameTime = now;
    this.frameDt = dt;

    this.updateCamera(world, alpha);
    // New run (any restart, same seed or not): the sim's shared id source
    // rewinds, so stale ribbons keyed by entity id would be adopted by the
    // new run's entities with the wrong params. Drop them.
    if (world.tick < this.lastWorldTick) this.trails.reset();
    this.lastWorldTick = world.tick;
    this.updatePlayerMesh(world, alpha);
    this.updateInstances(world, alpha);
    this.updateTrails(world, alpha);
    this.backgroundGrid.update(world.tick, alpha, this.camera);
    if (world.runSeed !== this.lastRunSeed) {
      this.lastRunSeed = world.runSeed;
      this.backgroundObjects.setSeed(world.runSeed);
      // New run, new ship: swap in this seed's procedurally generated hull,
      // shaped by the stored trait bias (all-zero until a loadout is set).
      this.rebuildShipHull(world.runSeed, this.shipBias);
    }
    this.backgroundObjects.update(world.tick, alpha, this.camera);
    this.portals.update(world.tick, alpha);
    this.deathRings.update(world.tick, alpha);
    this.microEvents.onMove({
      playerPos: this.lastPlayerPos,
      playerVel: world.player.vel,
    });
    this.microEvents.update(dt);
    this.playerDeathFX.update(dt);

    // Zone effect: show a pulsing force-field ring when the player is inside a
    // beneficial galaxy zone (healing = green, dash recharge = gold).
    let inHealingZone = false;
    let inDashZone = false;
    for (const z of this.getHealingZones()) {
      const d = wrappedDelta(this.lastPlayerPos, z);
      if (d.x * d.x + d.y * d.y <= z.radius * z.radius) {
        inHealingZone = true;
        break;
      }
    }
    if (!inHealingZone) {
      for (const z of this.getGalaxyZones()) {
        const d = wrappedDelta(this.lastPlayerPos, z);
        if (d.x * d.x + d.y * d.y <= z.radius * z.radius) {
          inDashZone = true;
          break;
        }
      }
    }
    this.zoneEffect.update(this.lastPlayerPos, inHealingZone, inDashZone, world.tick / 60 + alpha / 60);

    this.updateBonusVisuals(world, alpha);

    // Space storm: fog/thunder/firebolts, anchored to the camera and driven
    // entirely by the sim tick (plus interpolation alpha). Storm strength
    // also boosts the lomo edge blur so the edges go soft and unreadable.
    const stormViewW = this.camera.right - this.camera.left;
    this.spaceStormFX.update(
      this.spaceStormState,
      this.cameraAnchor,
      world.tick + alpha,
      stormViewW,
      this.viewHeight,
      this.reducedFlashes
    );
    this.postProcess.setStormLomoBoost(this.spaceStormFX.fogStrengthNow * 0.6);

    this.particles.update(dt);
    this.particles.finalize();
    this.trails.update(dt);
    this.damageNumbers.update(dt);
    this.applyTrauma(dt);

    if (this.dbgEnabled) {
      if (!this.dbgEl) {
        this.dbgEl = document.createElement('div');
        this.dbgEl.style.cssText =
          'position:fixed;left:4px;top:4px;z-index:9999;pointer-events:none;' +
          'font:9px monospace;color:#7dff4f;text-shadow:0 1px 0 #000;white-space:pre;';
        document.body.appendChild(this.dbgEl);
      }
      const err = world.events.lastError ? `\nERR ${world.events.lastError}` : '';
      this.dbgEl.textContent =
        `D${this.dbgDeaths} B${this.particles.dbgDeathBursts} P${this.particles.aliveCount}` + err;
    }

    this.postProcess.render(this.scene, this.camera);
  }

  dispose(): void {
    this.damageNumbers.dispose();
    this.microEvents.dispose();
    this.deathRings.dispose();
    this.playerDeathFX.dispose();
    this.playerHalo.dispose();
    this.magnetRing.dispose();
    this.portals.dispose();
    this.wormhole.dispose();
    this.exitPortal.dispose();
    this.warpFX.dispose();
    this.spaceStormFX.dispose();
    this.bonusTint.geometry.dispose();
    this.bonusTintMaterial.dispose();
    this.backgroundGrid.dispose();
    this.backgroundObjects.dispose();
    this.hpArcs.dispose();
    this.zoneEffect.mesh.geometry.dispose();
    (this.zoneEffect.mesh.material as THREE.Material).dispose();
    this.renderer.dispose();
    this.postProcess.dispose();
  }

  private createPlayerMesh(): THREE.Group {
    const group = new THREE.Group();
    // Yaw (heading) applied first, then roll/pitch in the yawed local frame:
    // roll = around the nose axis, loop = around the wing axis.
    group.rotation.order = 'ZYX';
    this.shipHull = this.buildShipHull(generateShipSpec(1));
    group.add(this.shipHull);

    // Damage chips: dark triangular patches that appear as the hull takes hits.
    // They give a rough visual read of remaining HP without a UI glance.
    const chipPositions: [number, number][] = [
      [0.55, 0.2],
      [-0.5, 0.3],
      [0.35, -0.3],
      [-0.3, -0.4],
      [0.15, 0.55],
      [-0.15, 0.6],
    ];
    for (const [x, y] of chipPositions) {
      const chipShape = new THREE.Shape();
      chipShape.moveTo(0, 0.12);
      chipShape.lineTo(0.12, -0.08);
      chipShape.lineTo(-0.12, -0.08);
      chipShape.closePath();
      const chipGeo = new THREE.ShapeGeometry(chipShape);
      const chipMat = new THREE.MeshBasicMaterial({
        color: 0x1a0f00,
        transparent: true,
        opacity: 0.9,
        depthWrite: false,
      });
      const chip = new THREE.Mesh(chipGeo, chipMat);
      // Chips track the smaller procedural hull.
      chip.scale.set(SHIP_SCALE, SHIP_SCALE, 1);
      chip.position.set(x * SHIP_SCALE, y * SHIP_SCALE, 0.04);
      chip.visible = false;
      this.damageChips.push(chip);
      this.chipFades.push(0);
      group.add(chip);
    }

    // War-life decal layer: studs/badges earned over the run, synced from
    // getShipDecorations(world). Kept as a direct child of the player mesh
    // (never inside the hull group) so rebuildShipHull leaves it alone; z
    // sits above the hull parts (≤0.04), below the turret (0.05).
    this.shipDecals.position.z = 0.045;
    group.add(this.shipDecals);

    // Short-range light so the player stands out without flooding the frame.
    const light = new THREE.PointLight(0xffd36b, 1.2, 10);
    light.position.z = 1;
    group.add(light);
    return group;
  }

  /**
   * Build a procedurally generated hull (body, core, outline, rim, cockpit,
   * engine block) from a ship spec. All flat parts share one Shape so clones
   * stay in sync; the spec's warm hue shifts the emissive slightly per run.
   */
  private buildShipHull(spec: ShipSpec): THREE.Group {
    const hull = new THREE.Group();
    const shape = shipShapeFromSpec(spec);
    const geo = new THREE.ShapeGeometry(shape);
    const emissive = new THREE.Color().setHSL(spec.hue, 0.85, 0.58);
    const mat = new THREE.MeshStandardMaterial({
      color: 0xfff6dc,
      emissive,
      emissiveIntensity: 1.7,
      roughness: 0.2,
      metalness: 0.3
    });
    hull.add(new THREE.Mesh(geo, mat));

    // Tiny warm core above the hull for a crisp silhouette without a large halo.
    const core = new THREE.Mesh(
      geo.clone(),
      new THREE.MeshBasicMaterial({ color: 0xffe9a8, transparent: true, opacity: 0.7 })
    );
    core.scale.set(0.18, 0.18, 1);
    core.position.z = 0.02;
    hull.add(core);

    // Dark outline behind the craft for contrast against bright enemies.
    const outline = new THREE.Mesh(
      geo.clone(),
      new THREE.MeshBasicMaterial({ color: 0x1a0f00, transparent: true, opacity: 0.85, depthWrite: false })
    );
    outline.scale.set(1.18, 1.18, 1);
    outline.position.z = -0.02;
    hull.add(outline);

    // Crisp white rim so the silhouette reads even inside dense swarms
    // (HDR-pushed so bloom catches it — the ship is always the brightest
    // thing on screen).
    const rimPoints = shape.getPoints(24).map((p) => new THREE.Vector3(p.x, p.y, 0));
    const rim = new THREE.LineLoop(
      new THREE.BufferGeometry().setFromPoints(rimPoints),
      new THREE.LineBasicMaterial({ color: new THREE.Color(0xfff6dc).multiplyScalar(1.6), transparent: true, opacity: 1.0, toneMapped: false, depthWrite: false })
    );
    rim.position.z = 0.03;
    hull.add(rim);

    // Cockpit dot (position/size varies per hull).
    const cockpit = new THREE.Mesh(
      new THREE.CircleGeometry(spec.cockpit.r * SHIP_SCALE, 10),
      new THREE.MeshBasicMaterial({ color: 0xfff6dc, toneMapped: false })
    );
    cockpit.position.set(spec.cockpit.x * SHIP_SCALE, spec.cockpit.y * SHIP_SCALE, 0.04);
    hull.add(cockpit);

    // Engine block: a short hot bar at the tail.
    const engine = new THREE.Mesh(
      new THREE.PlaneGeometry(spec.engine.halfWidth * 2 * SHIP_SCALE, 0.12 * SHIP_SCALE),
      new THREE.MeshBasicMaterial({ color: 0xffc45e, toneMapped: false })
    );
    engine.position.set(0, spec.engine.y * SHIP_SCALE, 0.01);
    hull.add(engine);

    // Barrel studs (striker trait): tiny gold circles along the wing edges.
    for (const stud of spec.studs) {
      const mark = new THREE.Mesh(
        new THREE.CircleGeometry(stud.r * SHIP_SCALE, 8),
        new THREE.MeshBasicMaterial({ color: 0xffd36b, toneMapped: false })
      );
      mark.position.set(stud.x * SHIP_SCALE, stud.y * SHIP_SCALE, 0.04);
      hull.add(mark);
    }

    return hull;
  }

  /** Swap in a freshly generated hull for a new run seed. */
  private rebuildShipHull(seed: number, bias?: ShipBias): void {
    if (this.shipHull) {
      this.playerMesh.remove(this.shipHull);
      this.shipHull.traverse((obj) => {
        const mesh = obj as THREE.Mesh;
        if (mesh.geometry) mesh.geometry.dispose();
        if (mesh.material) (mesh.material as THREE.Material).dispose();
      });
    }
    this.shipHull = this.buildShipHull(generateShipSpec(seed, bias));
    this.markShipLayer(this.shipHull);
    this.playerMesh.add(this.shipHull);
  }

  /**
   * Move every mesh/line under `root` into the transparent pass (opacity
   * untouched) so the ship paints over the instanced swarm via z-sorting.
   */
  private markShipLayer(root: THREE.Object3D): void {
    root.traverse((obj) => {
      const mesh = obj as THREE.Mesh;
      const material = (mesh as { material?: THREE.Material }).material;
      if (!material) return;
      material.transparent = true;
      material.depthWrite = false;
    });
  }

  /**
   * War-life decals (spec §3.3): studs and badges earned over the run,
   * synced from the sim's decoration list. Rebuilt only when the decoration
   * count changes, so steady-state frames cost one array read.
   */
  private syncShipDecals(world: WorldState): void {
    const decorations = getShipDecorations(world);
    if (decorations.length === this.shipDecalCount) return;
    this.shipDecalCount = decorations.length;
    for (const child of this.shipDecals.children) {
      (child as THREE.Mesh).geometry.dispose();
    }
    this.shipDecals.clear();
    for (const d of decorations) {
      // Stud reads as a 2–3 px diamond; badges as small 3×2 px patches.
      const geo =
        d.kind === 'stud' ? new THREE.PlaneGeometry(0.12, 0.12) : new THREE.PlaneGeometry(0.15, 0.1);
      const mark = new THREE.Mesh(geo, this.decalMaterials[d.kind]);
      if (d.kind === 'stud') mark.rotation.z = Math.PI / 4;
      // Decoration coords are hull-local ±0.5; scaled like the hull parts.
      mark.position.set(d.x * SHIP_SCALE, d.y * SHIP_SCALE, 0);
      this.shipDecals.add(mark);
    }
  }

  /**
   * Hold-ground turret: a small tank-style turret mounted on top of the ship.
   * The barrel rotates independently to track the aim direction; the whole
   * mesh scales with the sim's deploy_progress and gains complexity as laser
   * upgrades are taken (extra barrels, panels, scope).
   */
  private createTurretMesh(): THREE.Group {
    const group = new THREE.Group();
    group.scale.set(0.55, 0.55, 1);

    const baseGeo = new THREE.CircleGeometry(0.34, 12);
    const baseMat = new THREE.MeshStandardMaterial({
      color: 0x2a4a66,
      emissive: 0x1a6a8a,
      emissiveIntensity: 1.1,
      roughness: 0.4,
      metalness: 0.6
    });
    const base = new THREE.Mesh(baseGeo, baseMat);
    group.add(base);

    // Barrel extends along local +Y, matching the player-mesh nose convention
    // (rotation.z = atan2(aim.y, aim.x) - PI/2 aims it). Kept short and slim
    // so the turret reads as a compact gun, not a mast.
    const barrel = new THREE.Group();
    const barrelGeo = new THREE.PlaneGeometry(0.12, 0.7);
    barrelGeo.translate(0, 0.35, 0);
    const barrelMat = new THREE.MeshBasicMaterial({
      color: 0xd8f6ff,
      transparent: true,
      opacity: 1.0,
      toneMapped: false
    });
    barrel.add(new THREE.Mesh(barrelGeo, barrelMat));

    const tipGeo = new THREE.CircleGeometry(0.11, 8);
    tipGeo.translate(0, 0.7, 0);
    const tipMat = new THREE.MeshBasicMaterial({ color: 0xffffff, toneMapped: false });
    barrel.add(new THREE.Mesh(tipGeo, tipMat));

    group.add(barrel);
    this.turretBarrel = barrel;

    // Upgrade attachments: shown as laser upgrades are taken.
    const makeAttachment = (geo: THREE.BufferGeometry, color: number, x: number, y: number, z: number): THREE.Mesh => {
      const mat = new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: 0.9,
        toneMapped: false,
      });
      const m = new THREE.Mesh(geo, mat);
      m.position.set(x, y, z);
      m.visible = false;
      group.add(m);
      return m;
    };

    // Second barrel (shown at laser damage/rate rank 2+). Parented to the
    // barrel so it tracks the aim direction.
    this.turretAttachments.secondBarrel = makeAttachment(new THREE.PlaneGeometry(0.08, 0.6), 0x9fefff, 0.18, 0.35, 0.02);
    this.turretAttachments.secondBarrel.rotation.z = -0.15;
    this.turretBarrel.add(this.turretAttachments.secondBarrel);
    // Side panel (shown at laser rate rank 1+).
    this.turretAttachments.panel = makeAttachment(new THREE.PlaneGeometry(0.25, 0.1), 0x4db8ff, -0.3, 0, 0.02);
    this.turretAttachments.panel.rotation.z = 0.4;
    // Scope (shown at laser damage rank 1+). Also rides the barrel.
    this.turretAttachments.scope = makeAttachment(new THREE.CircleGeometry(0.08, 8), 0xffd36b, 0, 0.55, 0.03);
    this.turretBarrel.add(this.turretAttachments.scope);

    group.visible = false;
    return group;
  }

  private updateCamera(world: WorldState, alpha: number): void {
    // Torus arena: the camera follows the player across the seam via a
    // continuous anchor so the background and entities wrap smoothly instead
    // of snapping from +100 to -100.
    const target = this.interpPos(world.player.pos, world.player.vel, alpha);
    const d = wrappedDelta(target, this.cameraAnchor);
    this.cameraAnchor.x += d.x;
    this.cameraAnchor.y += d.y;
    // Camera LAG (playtest direction): the focus point trails BEHIND the
    // ship's motion — leading ahead felt like the camera ran away and made
    // mouse steering read as slowdown. The anchor itself stays glued to the
    // player, so entity unwrapping and the seam behavior above are untouched
    // — only the camera rides on top.
    const vel = world.player.vel;
    const speed = Math.hypot(vel.x, vel.y);
    const viewW = this.camera.right - this.camera.left;
    let leadTx = 0;
    let leadTy = 0;
    if (speed > 1e-4) {
      leadTx = -(vel.x / speed) * viewW * 0.5 * CAMERA_LEAD_FRACTION;
      leadTy = -(vel.y / speed) * this.viewHeight * 0.5 * CAMERA_LEAD_FRACTION;
    } else {
      // Parked: no heading to lead toward, so ease the focus toward the
      // centroid of on-screen enemies (in the anchor's unwrapped frame) —
      // at high density this keeps the swarm framed instead of stranded in
      // a corner. One extra pass over the enemy pool, same cost class as the
      // instance pass below.
      const halfW = viewW * 0.5;
      const halfH = this.viewHeight * 0.5;
      let cx = 0;
      let cy = 0;
      let n = 0;
      for (const e of world.enemies.alive()) {
        const off = wrappedDelta(e.pos, this.cameraAnchor);
        if (Math.abs(off.x) > halfW || Math.abs(off.y) > halfH) continue;
        cx += off.x;
        cy += off.y;
        n++;
      }
      if (n > 0) {
        leadTx = (cx / n) * CAMERA_LEAD_SWARM_WEIGHT;
        leadTy = (cy / n) * CAMERA_LEAD_SWARM_WEIGHT;
      }
    }
    const leadMaxX = viewW * CAMERA_LEAD_MAX_FRACTION;
    const leadMaxY = this.viewHeight * CAMERA_LEAD_MAX_FRACTION;
    if (leadTx > leadMaxX) leadTx = leadMaxX;
    else if (leadTx < -leadMaxX) leadTx = -leadMaxX;
    if (leadTy > leadMaxY) leadTy = leadMaxY;
    else if (leadTy < -leadMaxY) leadTy = -leadMaxY;
    const leadK = 1 - Math.exp(-this.frameDt / CAMERA_LEAD_TAU_S);
    this.cameraLead.x += (leadTx - this.cameraLead.x) * leadK;
    this.cameraLead.y += (leadTy - this.cameraLead.y) * leadK;
    this.camera.position.x = this.cameraAnchor.x + this.cameraLead.x;
    this.camera.position.y = this.cameraAnchor.y + this.cameraLead.y;
    this.camera.lookAt(this.camera.position.x, this.camera.position.y, 0);
  }

  private updatePlayerMesh(world: WorldState, alpha: number): void {
    // The dead ship is gone — the death blaze owns the wreck site. Restored
    // automatically on restart (alive flips back to true).
    this.playerMesh.visible = world.player.alive;
    // Sim-derived time keeps all idle/dash animation deterministic.
    const t = world.tick / 60 + alpha / 60;
    const pos = this.interpPos(world.player.pos, world.player.vel, alpha);
    this.lastPlayerPos.x = pos.x;
    this.lastPlayerPos.y = pos.y;
    const dashing = world.player.dash.active;
    // Subtle idle bob; suppressed while dashing so the lunge reads cleanly.
    const bob = dashing ? 0 : Math.sin(t * 2.2) * 0.05;
    // The player mesh always sits at the camera anchor (screen center); the
    // wrapped sim position only matters for collision, not for rendering.
    this.playerMesh.position.set(this.cameraAnchor.x, this.cameraAnchor.y + bob, 0.1);
    const aimDir = dashing
      ? world.player.dash.dir
      : world.player.focus_aim.held ? world.player.focus_aim.dir : world.player.vel;
    const dt = this.frameDt;
    // Dash rising edge: throw a full aileron roll ("barrel on flash").
    const dashStart = dashing && !this.prevDashActive;
    this.prevDashActive = dashing;
    if (dashStart && !this.maneuver && (aimDir.x !== 0 || aimDir.y !== 0)) {
      this.maneuver = { kind: 'roll', t: 0, duration: 0.4 };
      this.particles.spawnDust(this.lastPlayerPos, { x: -aimDir.y, y: aimDir.x });
      this.particles.spawnDust(this.lastPlayerPos, { x: aimDir.y, y: -aimDir.x });
    }
    if (aimDir.x !== 0 || aimDir.y !== 0) {
      const target = Math.atan2(aimDir.y, aimDir.x) - Math.PI / 2;
      let delta = target - this.shipHeading;
      while (delta > Math.PI) delta -= 2 * Math.PI;
      while (delta < -Math.PI) delta += 2 * Math.PI;
      // Heading eases (fast, so control stays tight).
      this.shipHeading += delta * Math.min(1, dt * 14);
      // Jet banking: roll into the turn proportional to its sharpness, then
      // ease back to level on straight flight.
      const targetBank = Math.max(-0.5, Math.min(0.5, delta * 2.0));
      this.bankAngle += (targetBank - this.bankAngle) * Math.min(1, dt * 8);
    } else {
      this.bankAngle *= Math.max(0, 1 - dt * 8);
    }

    let rollAngle = 0;
    let bobScale = 1;
    let arcZ = 0;
    if (this.maneuver) {
      this.maneuver.t += dt;
      const k = Math.min(1, this.maneuver.t / this.maneuver.duration);
      // Aileron roll through the dash: a full 360° around the nose axis with
      // a small altitude arc.
      rollAngle = Math.PI * 2 * smoothstep(k);
      arcZ = Math.sin(k * Math.PI) * 0.5;
      bobScale = 1 - Math.sin(k * Math.PI) * 0.1;
      if (k >= 1) this.maneuver = null;
    }
    this.playerMesh.position.z = 0.1 + arcZ;
    this.playerMesh.rotation.y = this.bankAngle + rollAngle;
    this.playerMesh.rotation.z = this.shipHeading;

    // Hold-ground turret: mounted on top of the ship like a space tank — it
    // IS the manual gun, so it stays visible and its barrel slews toward the
    // pointer (click/tap location) or, failing that, the ship's heading.
    // Deploy progress braces it (slightly larger, ready to fire the buffed
    // cannon) instead of toggling visibility.
    const turretProgress = world.turret.deploy_progress;
    this.turretMesh.visible = true;
    // Compact brace: the turret sits small on the hull; deploy progress
    // still braces it slightly upward.
    const brace = 0.46 + smoothstep(turretProgress) * 0.1;
    this.turretMesh.scale.set(brace, brace, 1);
    const aimSrc =
      world.player.focus_aim.dir.x !== 0 || world.player.focus_aim.dir.y !== 0
        ? world.player.focus_aim.dir
        : world.player.vel;
    if (aimSrc.x !== 0 || aimSrc.y !== 0) {
      const target = Math.atan2(aimSrc.y, aimSrc.x) - Math.PI / 2;
      let delta = target - this.turretAimAngle;
      while (delta > Math.PI) delta -= 2 * Math.PI;
      while (delta < -Math.PI) delta += 2 * Math.PI;
      this.turretAimAngle += delta * 0.25;
    }
    this.turretBarrel.rotation.z = this.turretAimAngle - this.playerMesh.rotation.z;
    // Turret complexity grows with laser upgrades: scope at damage 1+,
    // panel at rate 1+, second barrel at damage 2+ or pierce 1+.
    const mods = getUpgradeMods(world.player);
    this.turretAttachments.scope.visible = mods.laserDamageMult > 0;
    this.turretAttachments.panel.visible = mods.laserRateMult > 0;
    this.turretAttachments.secondBarrel.visible = mods.laserDamageMult >= 0.4 || mods.laserPierceBonus > 0;
    // Idle scale pulse; dash squash-and-stretch along the craft nose (local Y),
    // which aligns with the dash direction while dashing. The maneuver
    // altitude dip folds in here (position.z carries the arc itself).
    const pulse = (1 + Math.sin(t * 2.2) * 0.025) * bobScale;
    if (dashing) {
      this.playerMesh.scale.set(0.82 * pulse, 1.22 * pulse, 1);
    } else {
      this.playerMesh.scale.set(pulse, pulse, 1);
    }

    // Damage chips: show more chips as HP drops, with eased fade-in/out.
    const hpPct = world.player.max_hp > 0 ? world.player.hp / world.player.max_hp : 0;
    const chipsToShow = hpPct > 0.75 ? 0 : hpPct > 0.5 ? 2 : hpPct > 0.25 ? 4 : 6;
    const fadeSpeed = 6; // ~0.17 s to full
    for (let i = 0; i < this.damageChips.length; i++) {
      const target = i < chipsToShow ? 1 : 0;
      const fade = this.chipFades[i];
      if (fade < target) {
        this.chipFades[i] = Math.min(target, fade + fadeSpeed * (1 / 60));
      } else if (fade > target) {
        this.chipFades[i] = Math.max(target, fade - fadeSpeed * (1 / 60));
      }
      const f = easeOutCubic(this.chipFades[i]);
      this.damageChips[i].visible = f > 0.001;
      const mat = this.damageChips[i].material as THREE.MeshBasicMaterial;
      mat.opacity = f * 0.9;
    }
    const hullMesh = this.playerMesh.children[0] as THREE.Mesh | undefined;
    if (hullMesh && hullMesh.material instanceof THREE.MeshStandardMaterial) {
      const base = new THREE.Color(0xfff6dc);
      const damaged = new THREE.Color(0x8a5a4a);
      hullMesh.material.color.copy(base).lerp(damaged, 1 - hpPct);
    }

    // Boon visualization: update on-ship attachments from acquired boons.
    this.boonVisuals.update(world.player, t);
    // Permanent halo: rides under the hull so the ship stays the brightest,
    // most findable object on screen even at 200-enemy density.
    this.playerHalo.update(
      { x: this.cameraAnchor.x, y: this.cameraAnchor.y + bob },
      t,
      world.player.alive
    );
    // Art-direction trim: the aura flooded the hull pixels it marks. Shrunk +
    // dimmed three times (stock → ×0.62/×0.6, −25% under the tighter bloom
    // discipline, then another ~10% after the round-3 review: the ship's
    // immediate vicinity must stay surgically clean — GW:RE2 idiom). It
    // still spots the ship under a converged pile; the hull stays readable.
    this.playerHalo.mesh.scale.multiplyScalar(0.44);
    (this.playerHalo.mesh.material as THREE.MeshBasicMaterial).opacity *= 0.38;
    // Dashed ring at the effective magnet radius: rides with the halo.
    this.magnetRing.update(
      { x: this.cameraAnchor.x, y: this.cameraAnchor.y + bob },
      world.magnetPullRadius,
      world.player.alive
    );
    // War-life decals: rebuilt only when the decoration count changes.
    this.syncShipDecals(world);
  }

  private updateInstances(world: WorldState, alpha: number): void {
    const time = world.tick / 60 + alpha / 60;
    const tickF = world.tick + alpha;
    this.instances.reset();
    // Render positions are unwrapped into the camera's local frame so entities
    // straddling the torus seam draw on the correct side without a jump.
    const cam = this.cameraAnchor;
    for (const e of world.enemies.alive()) {
      const renderPos = { x: cam.x + wrappedDelta(e.pos, cam).x, y: cam.y + wrappedDelta(e.pos, cam).y };
      const telegraphing = e.telegraph_until > world.tick;
      enemyMatrix(e, time, this.dummyMatrix, telegraphing, renderPos);
      // Spawn-in scale: 0→1 over the portal-open window. Derived from the sim
      // tick (plus interpolation alpha), so replays scale in identically.
      const spawnT = (tickF - e.spawn_tick) / PORTAL_OPEN_TICKS;
      if (spawnT < 1) {
        const f = spawnT <= 0 ? 0.0001 : 1 - Math.pow(1 - spawnT, 3);
        this.spawnScaleMatrix.makeScale(f, f, 1);
        this.dummyMatrix.multiply(this.spawnScaleMatrix);
      }
      // Portrait sanity pass: enemies render slightly smaller vs the ship on
      // tall/narrow screens (visual only — sim hitboxes are untouched).
      if (this.enemyViewScale !== 1) {
        this.spawnScaleMatrix.makeScale(this.enemyViewScale, this.enemyViewScale, 1);
        this.dummyMatrix.multiply(this.spawnScaleMatrix);
      }
      enemyColor(e, this.dummyColor);
      if (telegraphing) {
        // Bright, fast-pulsing telegraph so imminent spawns are unmistakable.
        const k = 0.55 + 0.3 * Math.sin(time * 10 + e.pool_index * 1.7);
        this.dummyColor.lerp(WHITE, k);
      }
      // Hit blink: a struck enemy flashes white for ~0.1 s (GW-style read),
      // replacing the old floating damage triangles.
      const hitTick = this.enemyHitTicks.get(e.id);
      if (hitTick !== undefined) {
        const hitAge = world.tick - hitTick;
        if (hitAge >= 0 && hitAge < 6) {
          this.dummyColor.lerp(WHITE, 0.75 * (1 - hitAge / 6));
        } else {
          this.enemyHitTicks.delete(e.id);
        }
      }
      this.instances.addEnemy(e, this.dummyMatrix, this.dummyColor, time);
    }
    for (const p of world.projectiles.alive()) {
      const renderPos = { x: cam.x + wrappedDelta(p.pos, cam).x, y: cam.y + wrappedDelta(p.pos, cam).y };
      projectileMatrix(p, this.dummyMatrix, renderPos);
      this.instances.projectiles.add(this.dummyMatrix, PROJECTILE_COLOR);
    }
    for (const p of world.pickups.alive()) {
      const renderPos = { x: cam.x + wrappedDelta(p.pos, cam).x, y: cam.y + wrappedDelta(p.pos, cam).y };
      pickupMatrix(p, time, this.dummyMatrix, renderPos);
      this.instances.addPickup(p, this.dummyMatrix, pickupColor(p, time), time);
    }
    this.instances.finalize();
    // Thin HP arcs over every damaged enemy (mint → gold → red).
    this.hpArcs.update(world.enemies.alive(), cam, time);
  }

  private updateTrails(world: WorldState, alpha: number): void {
    // Trails store positions in the camera's unwrapped frame (same transform
    // as the instanced sprites) so ribbons stay glued to their entity across
    // the torus seam instead of drawing map-long segments.
    this.trails.setCameraAnchor(this.cameraAnchor);
    this.trails.updateFromWorld(
      world.player,
      world.projectiles.alive(),
      world.enemies.alive(),
      world.pickups.alive(),
      world.tick / 60 + alpha / 60
    );
  }

  /**
   * Bonus-game layer + wormhole portal: tick-derived fade envelopes drive
   * the background hue tint and circular field boundary (bonus game only)
   * and the wormhole portal (whenever a wormhole is active — normal play
   * included). Everything keys off world.tick + alpha, never the wall clock.
   */
  /**
   * Wormhole subspace visuals. Everything is derived from sim state
   * (world.wormhole + world.bonus) on the sim tick — no pushed render state:
   * - Subspace mix (0..1): the grid palette + fullscreen tint crossfade.
   *   Ramps with the warp-in, holds through the fight/boon, ramps back with
   *   the warp-out (WARP_IN/OUT_TICKS own the timing in sim/bonus-game.ts).
   * - Warp tunnel overlay: intensity sin(π·progress) so the planes swap at
   *   the white-hot peak; direction flips on the way home.
   * - Entrance wormhole + exit portal vortices with their open envelopes and
   *   post-use collapse animations (collapse_tick on the sim state).
   */
  private updateBonusVisuals(world: WorldState, alpha: number): void {
    const tickF = world.tick + alpha;
    const time = tickF / 60;
    const b = world.bonus;

    // Phase progress → subspace mix + warp envelope.
    let subMix = 0;
    let warp = 0;
    let warpDir = 1;
    if (b.phase === 'entering') {
      const p = Math.min(1, Math.max(0, (tickF - b.phase_start_tick) / WARP_IN_TICKS));
      warp = Math.sin(Math.PI * p);
      subMix = smoothstep01((p - 0.15) / 0.7);
    } else if (b.phase === 'fighting' || b.phase === 'boon') {
      subMix = 1;
    } else if (b.phase === 'exiting') {
      const p = Math.min(1, Math.max(0, (tickF - b.phase_start_tick) / WARP_OUT_TICKS));
      warp = Math.sin(Math.PI * p);
      warpDir = -1;
      subMix = 1 - smoothstep01((p - 0.15) / 0.7);
    }

    this.backgroundGrid.setSubspace(subMix);

    // Fullscreen hue tint: follows the subspace mix exactly (continuous
    // across phase flips by construction), slightly stronger than the old
    // arena bonus tint — this is another place, not a game mode banner.
    const tintStrength = subMix;
    this.bonusTint.visible = tintStrength > 0.004;
    if (this.bonusTint.visible) {
      this.bonusTint.position.set(this.cameraAnchor.x, this.cameraAnchor.y, 0.02);
      const viewW = this.camera.right - this.camera.left;
      this.bonusTint.scale.set(viewW + 8, this.viewHeight + 8, 1);
      this.bonusTintMaterial.uniforms.uTime.value = time;
      this.bonusTintMaterial.uniforms.uStrength.value = tintStrength;
    }

    // Warp tunnel overlay (peak mid-transition).
    this.warpFX.update(
      this.cameraAnchor,
      this.camera.right - this.camera.left,
      this.viewHeight,
      time,
      warp,
      warpDir,
      subMix
    );

    // Entrance wormhole: open while active; collapse animation after entry.
    const wh = world.wormhole;
    let whCollapse = 0;
    if (!wh.active && wh.collapse_tick >= 0 && tickF - wh.collapse_tick < PORTAL_COLLAPSE_TICKS) {
      whCollapse = (tickF - wh.collapse_tick) / PORTAL_COLLAPSE_TICKS;
    }
    const whStrength = easePortalEnvelope(this.wormholeEnv, wh.active || whCollapse > 0, world.tick, tickF);
    this.updatePortalVisual(this.wormhole, wh.pos, whStrength, whCollapse, time);

    // Subspace exit portal: open while active; collapse after use/exit.
    const ep = b.exit_portal;
    let epCollapse = 0;
    if (!ep.active && ep.collapse_tick >= 0 && tickF - ep.collapse_tick < PORTAL_COLLAPSE_TICKS) {
      epCollapse = (tickF - ep.collapse_tick) / PORTAL_COLLAPSE_TICKS;
    }
    const epStrength = easePortalEnvelope(this.exitEnv, ep.active || epCollapse > 0, world.tick, tickF);
    this.updatePortalVisual(this.exitPortal, ep.pos, epStrength, epCollapse, time);
  }

  /** Portal vortex at its sim position, unwrapped into the camera frame like
   * any other entity straddling the torus seam. */
  private updatePortalVisual(
    portal: WormholePortal,
    pos: Vec2,
    strength: number,
    collapse01: number,
    time: number
  ): void {
    const d = wrappedDelta(pos, this.cameraAnchor);
    portal.update({ x: this.cameraAnchor.x + d.x, y: this.cameraAnchor.y + d.y }, time, strength, collapse01);
  }

  /** Fullscreen subspace murk: deep violet/red vignette; the center stays
   * readable, the edges sink into the other plane. */
  private createBonusTintMaterial(): THREE.ShaderMaterial {
    return new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uStrength: { value: 0 },
      },
      vertexShader: /* glsl */ `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        precision highp float;
        uniform float uTime;
        uniform float uStrength;
        varying vec2 vUv;

        void main() {
          if (uStrength <= 0.004) discard;
          vec2 p = vUv * 2.0 - 1.0;
          float rad = length(p * vec2(0.75, 1.0));
          // Hue drifts violet <-> ember red on sim time alone.
          float hueMix = 0.5 + 0.5 * sin(uTime * 0.55);
          vec3 col = mix(vec3(0.42, 0.10, 0.72), vec3(0.68, 0.12, 0.30), hueMix);
          // Subtle at the center so gameplay stays readable, heavier at the
          // edges like a vignette.
          float edge = smoothstep(0.25, 1.15, rad);
          float alpha = uStrength * mix(0.08, 0.42, edge);
          gl_FragColor = vec4(col, alpha);
        }
      `,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      blending: THREE.NormalBlending,
    });
  }

  // Turret-fire recoil: a small directional camera offset, kicked AWAY from
  // the shot direction and springing back fast (applyTrauma owns the decay).
  private recoilX = 0;
  private recoilY = 0;

  /** Kick the camera opposite `dir` — the weak отдача of the deployed turret. */
  addRecoil(dirX: number, dirY: number): void {
    const len = Math.hypot(dirX, dirY);
    if (len < 1e-6) return;
    this.recoilX -= (dirX / len) * 0.22;
    this.recoilY -= (dirY / len) * 0.22;
  }

  private applyTrauma(dt: number): void {
    // Turret recoil (weak отдача): a small offset kicked opposite the shot
    // direction, springing back over ~0.1 s. Runs before the trauma guard.
    if (this.recoilX !== 0 || this.recoilY !== 0) {
      const scale = this.reducedShake ? 0.4 : 1;
      this.camera.position.x += this.recoilX * scale;
      this.camera.position.y += this.recoilY * scale;
      const k = Math.exp(-dt / 0.07);
      this.recoilX *= k;
      this.recoilY *= k;
      if (Math.abs(this.recoilX) < 0.004 && Math.abs(this.recoilY) < 0.004) {
        this.recoilX = 0;
        this.recoilY = 0;
      }
    }
    if (this.trauma <= 0) return;
    // Ease the shake so impacts decay naturally instead of linearly.
    const eased = easeOutCubic(this.trauma);
    const shake = eased * eased * (this.reducedShake ? 0.125 : 0.5);
    this.camera.position.x += (Math.random() - 0.5) * shake;
    this.camera.position.y += (Math.random() - 0.5) * shake;
    if (!this.reducedShake) {
      this.camera.rotation.z = (Math.random() - 0.5) * eased * 0.0087; // ~0.5 deg
    }
    this.trauma = Math.max(0, this.trauma - dt * 1.2);
  }

  private interpPos(pos: Vec2, vel: Vec2, alpha: number): Vec2 {
    return {
      x: pos.x + vel.x * alpha / 60,
      y: pos.y + vel.y * alpha / 60
    };
  }
}
